import { useEffect, useRef } from "react";
import { useResolvedTheme } from "@/lib/theme";

// Cloudflare Turnstile on the login page (DECISIONS.md ADR-0049). The
// script is loaded only here, on demand; CSP allows challenges.cloudflare.com
// for script-src and frame-src (web/deploy/nginx.conf.template).

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Must match internal/turnstile.Action: Master refuses tokens for any other action. */
export const TURNSTILE_ACTION = "login";

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

const WIDGET_SCALE = 0.9;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile missing")));
    s.onerror = () => {
      loading = null;
      reject(new Error("không tải được Cloudflare Turnstile"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * Renders the widget and reports its token: a string once solved, null
 * while unsolved, expired or failed (Turnstile refreshes expired tokens by
 * itself).
 */
export function TurnstileWidget({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
  onError?: (message: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const theme = useResolvedTheme();
  const cb = useRef({ onToken, onError });
  useEffect(() => {
    cb.current = { onToken, onError };
  });

  useEffect(() => {
    let id: string | null = null;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !box.current) return;
        id = ts.render(box.current, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          theme,
          language: "vi",
          size: "normal",
          "refresh-expired": "auto",
          callback: (token: string) => cb.current.onToken(token),
          "expired-callback": () => cb.current.onToken(null),
          "error-callback": () => {
            cb.current.onToken(null);
            cb.current.onError?.("Không xác minh được, đang thử lại…");
          },
        });
      })
      .catch((e: Error) => cb.current.onError?.(e.message));
    return () => {
      cancelled = true;
      if (id && window.turnstile) window.turnstile.remove(id);
    };
  }, [siteKey, theme]);

  // The widget is a fixed 300×65 iframe we cannot style. Show it at 90%,
  // centered, and crop its 1px light frame inside a rounded box with our
  // own hairline, so it sits on the card instead of floating as a grey slab.
  return (
    <div className="mx-auto h-[57px] w-[268px] overflow-hidden rounded-[5px] bg-surface-2 ring-1 ring-line">
      <div
        ref={box}
        className="h-[65px] w-[300px] origin-top-left"
        style={{ transform: `scale(${WIDGET_SCALE}) translate(-1px, -1px)` }}
        data-testid="turnstile"
      />
    </div>
  );
}
